// The app supplies UI, logs and re-engagement analytics used across Dictate.
//
// Each emitter is supplied by reference so its routing remains explicit.

import type { ButtonAction } from '@heidi/analytics/vocabulary';
import type {
  DictationLogsAnalyticsPorts,
  DictationReengagementAnalyticsPorts,
} from './contracts';

/**
 * The TrackedButton emitter is typed HERE because its signature names the
 * vocabulary package's enum and the contracts door is relative-import-only.
 */
export interface DictationUiTrackingPorts {
  /**
   * The app's `TrackedButton` emit: `button_clicked` with the action and the
   * button's visible label, no screen name — a different payload from
   * `trackButtonClicked`, so it remains an app-side member.
   */
  trackUnscopedButtonClicked: (action: ButtonAction, label: string) => void;
}

/** One member per analytics surface the deferred dictation cone emits on. */
export interface DictationAnalyticsPorts {
  /** TrackedButton's unscoped `button_clicked` emit. */
  ui: DictationUiTrackingPorts;
  /** The Dictate-history surfaces (`/dictation-logs` and its tabs). */
  logs: DictationLogsAnalyticsPorts;
  /** The dictate re-engagement hints and general card. */
  reengagement: DictationReengagementAnalyticsPorts;
}

let ports: DictationAnalyticsPorts | null = null;

export const configureDictationAnalyticsPorts = (next: DictationAnalyticsPorts): void => {
  ports = next;
};

export const getDictationAnalyticsPorts = (): DictationAnalyticsPorts => {
  if (!ports) {
    throw new Error(
      '[dictation/analytics-ports] ports not configured — the app must load ' +
        "'@/composition/hosts/dictation/dictation-analytics-wiring' at bootstrap; tests must call " +
        'configureDictationAnalyticsPorts() with a double first.'
    );
  }
  return ports;
};

export const resetDictationAnalyticsPortsForTests = (): void => {
  ports = null;
};
